import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/AuthContext';
import { requestRecovery } from '@/features/auth/api';
import { ApiError } from '@/lib/apiClient';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Icon } from '@/components/ui/Icon';
import { Spinner } from '@/components/ui/Spinner';
import { PoweredByApexium } from '@/components/ui/PoweredByApexium';

const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'];

/** Códigos are 4–6 digits. The seeded bootstrap ones are 6, so a hard 4 locks
 *  the very first sign-in out of the app. Keep both ends in sync with
 *  CodigoSheet, MustChangePage and the Identity password policy. */
const CODIGO_MIN = 4;
const CODIGO_MAX = 6;

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();

  const [screen, setScreen] = useState<'login' | 'recover'>('login');
  const [username, setUsername] = useState('');
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const pronto = username.trim().length > 1 && codigo.length >= CODIGO_MIN;

  const tap = (key: string) => {
    setError(null);
    setCodigo((current) =>
      key === 'C' ? '' : key === '⌫' ? current.slice(0, -1) : (current + key).slice(0, CODIGO_MAX),
    );
  };

  const submit = async () => {
    if (!pronto || busy) return;
    setBusy(true);
    setError(null);
    try {
      await login(username, codigo);
    } catch (err) {
      setCodigo('');
      setError(err instanceof ApiError ? err : new ApiError(0, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  if (screen === 'recover') {
    return <RecoverScreen onBack={() => setScreen('login')} />;
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-conduril px-5 py-10">
      <div className="w-full max-w-app lg:grid lg:max-w-4xl lg:grid-cols-2 lg:items-center lg:gap-14">
        {/* Brand column — only worth the space once there is space. */}
        <div className="hidden lg:block">
          <img src="/logo-white.svg" alt="Partes Diárias" className="h-14 w-auto" />
          <h1 className="mt-8 text-[38px] font-semibold leading-tight tracking-tight text-white">
            <b className="font-bold">Partes</b> Diárias
          </h1>
          <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-conduril-soft">
            {t('login.tagline')}
          </p>
          <PoweredByApexium tone="dark" className="mt-12 !justify-start" />
        </div>

        {/* Form column */}
        <div>
      <div className="animate-fade-in rounded-[22px] bg-white px-5 pb-6 pt-6 shadow-2xl">
        <img src="/logo.svg" alt="Partes Diárias" className="mx-auto block h-14 w-auto lg:hidden" />
        <div className="mt-4 text-center text-[21px] leading-tight tracking-tight text-conduril-ink lg:mt-0 lg:text-left">
          <b className="font-bold">Partes</b> Diárias
        </div>

        <label className="mt-6 block text-[13px] font-bold text-[#2b3040]" htmlFor="username">
          {t('login.username')}
        </label>
        <input
          id="username"
          className="field mt-2"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
        />

        <div className="mt-4 flex items-center justify-between">
          <span className="text-[13px] font-bold text-[#2b3040]">{t('login.code')}</span>
          <span className="num h-[18px] text-[18px] font-semibold tracking-[0.4em] text-conduril">
            {'•'.repeat(codigo.length)}
          </span>
        </div>

        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {KEYPAD.map((key) => {
            const acao = key === 'C' || key === '⌫';
            return (
              <button
                key={key}
                type="button"
                onClick={() => tap(key)}
                className={`num rounded-xl border py-3 text-[17px] font-semibold transition active:scale-[0.97] ${
                  acao
                    ? 'border-[#e8eaf1] bg-[#f6f7fa] text-[#7c8393]'
                    : 'border-[#dcdfe8] bg-white text-conduril'
                }`}
              >
                {key}
              </button>
            );
          })}
        </div>

        {error != null && (
          <div className="mt-4">
            <ErrorBanner error={error} />
          </div>
        )}

        <button
          type="button"
          disabled={!pronto || busy}
          onClick={() => void submit()}
          className="btn-primary mt-5 shadow-btn disabled:shadow-none"
        >
          {busy ? <Spinner className="border-white/40 border-t-white" /> : t('login.submit')}
        </button>

        <button
          type="button"
          onClick={() => setScreen('recover')}
          className="mt-3 w-full py-1 text-[12px] font-medium text-[#5b6480] transition hover:text-conduril"
        >
          {t('login.forgot')}
        </button>
      </div>

      <PoweredByApexium tone="dark" className="mt-7 lg:hidden" />
        </div>
      </div>
    </div>
  );
}

function RecoverScreen({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const [identificador, setIdentificador] = useState('');
  const [telefone, setTelefone] = useState('');
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pronto = identificador.trim().length >= 3;

  const submit = async () => {
    if (!pronto || busy) return;
    setBusy(true);
    try {
      const res = await requestRecovery(identificador.trim(), telefone || undefined);
      setMensagem(res.mensagem);
    } catch {
      // Recovery never reveals whether the account exists — the same reassuring
      // line is shown either way.
      setMensagem(t('login.recoverLead'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-app flex-col bg-conduril px-6 pb-7 pt-14 md:max-w-lg md:justify-center md:pt-7">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 self-start rounded-full border border-white/20 bg-white/10 py-1.5 pl-2 pr-3 text-[10px] font-medium uppercase tracking-widest text-white"
      >
        <Icon name="arrow_back" className="text-[15px]" /> {t('common.back')}
      </button>

      {mensagem ? (
        <div className="mt-14 flex animate-fade-in flex-col items-center text-center">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-white/15 text-white">
            <Icon name="mark_email_read" className="text-[32px]" />
          </span>
          <h2 className="mt-6 text-[21px] font-semibold text-white">{t('login.recoverSentTitle')}</h2>
          <p className="mt-2.5 text-[13px] leading-relaxed text-[#a9b4e6]">{mensagem}</p>
          <button
            type="button"
            onClick={onBack}
            className="mt-7 rounded-2xl bg-white px-6 py-3.5 text-[13px] font-semibold text-conduril"
          >
            {t('login.backToStart')}
          </button>
        </div>
      ) : (
        <>
          <h2 className="mt-9 text-[23px] font-semibold leading-tight text-white">
            {t('login.recoverTitle')}
          </h2>
          <p className="mt-2.5 text-[13px] leading-relaxed text-[#a9b4e6]">{t('login.recoverLead')}</p>

          <label className="mt-7 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8492d4]">
            {t('login.recoverField')}
          </label>
          <input
            className="num mt-2.5 w-full rounded-xl border border-white/25 bg-white/10 px-3.5 py-3.5 text-[15px] tracking-wide text-white outline-none placeholder:text-white/40"
            autoCapitalize="none"
            spellCheck={false}
            value={identificador}
            onChange={(e) => setIdentificador(e.target.value.toLowerCase().replace(/\s/g, '').slice(0, 32))}
          />

          <label className="mt-4 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8492d4]">
            {t('login.recoverPhone')}
          </label>
          <input
            className="num mt-2.5 w-full rounded-xl border border-white/25 bg-white/10 px-3.5 py-3.5 text-[15px] font-semibold tracking-widest text-white outline-none placeholder:text-white/40"
            placeholder={t('login.recoverPhonePlaceholder')}
            inputMode="numeric"
            value={telefone}
            onChange={(e) => setTelefone(e.target.value.replace(/\D/g, '').slice(0, 3))}
          />

          <button
            type="button"
            disabled={!pronto || busy}
            onClick={() => void submit()}
            className="mt-6 w-full rounded-2xl bg-white px-4 py-4 text-[14px] font-semibold text-conduril
                       disabled:bg-white/15 disabled:text-white/45"
          >
            {busy ? t('common.loading') : t('login.recoverSubmit')}
          </button>
        </>
      )}

      <PoweredByApexium tone="dark" className="mt-auto pt-8" />
    </div>
  );
}
